import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Trash2, Droplet } from "lucide-react";
import { ITEM_TYPES, EMPTY_ITEM } from "./constants";
import PSACategoryIcon from "../../components/PSACategoryIcon";

// Categorii PSA în ordinea cerută de user: 1.Mască 2.Furtun 3.Curea 4.Cizme 5.Hanorac 6.Tricou 7.Bocanci 8.Pantaloni
const PSA_CATEGORIES_BUILTIN = [
  { code: "echipament_complet", label: "Echipament COMPLET (Mască + Curea + Furtun)", needs_size: false, is_set: true, is_respiratory: true },
  { code: "masca", label: "Mască", needs_size: true, is_respiratory: true },
  { code: "furtun", label: "Furtun", needs_size: false, is_respiratory: true },
  { code: "curea", label: "Curea", needs_size: false, is_respiratory: true },
  { code: "casca", label: "Cască", needs_size: true },
  { code: "cizme", label: "Cizme", needs_size: true },
  { code: "hanorac", label: "Hanorac", needs_size: true },
  { code: "tricou", label: "Tricou", needs_size: true },
  { code: "geaca", label: "Geacă", needs_size: true },
  { code: "bocanci", label: "Bocanci", needs_size: true },
  { code: "pantaloni", label: "Pantaloni", needs_size: true },
  { code: "altele", label: "Altele", needs_size: false },
];

// Folosit pentru fallback când meta lipsește. Util și pentru `is_respiratory` (nu vine din backend)
const BUILTIN_RESPIRATORY = new Set(["echipament_complet", "masca", "furtun", "curea"]);

function getPsaCategories(meta) {
  const list = meta?.psa_categories;
  if (Array.isArray(list) && list.length > 0) {
    // Re-add `is_respiratory` din built-in (backend nu returnează acest flag)
    return list.map((c) => ({ ...c, is_respiratory: BUILTIN_RESPIRATORY.has(c.code) }));
  }
  return PSA_CATEGORIES_BUILTIN;
}


// Tipuri de echipament cu iconițe & culori pentru selectorul vizual
const ITEM_TYPE_META = {
  echipament: { icon: "🔧", label: "Echipament / Sculă", desc: "scule, dispozitive, aparate (cu serie, culoare echipă)", color: "blue" },
  psa:        { icon: "🦺", label: "PSA",                  desc: "echipament de protecție personală (mască, tricou, cizme...)", color: "cyan" },
  consumabil: { icon: "📦", label: "Consumabil",           desc: "ulei, antigel, filtre, piese de schimb...", color: "amber" },
  piesa_auto: { icon: "🚗", label: "Piesă auto",           desc: "anvelope, kit ambreiaj, etc. legate de o mașină", color: "emerald" },
};

const RESPIRATORY_MODELS = ["Sundström", "3M", "Altele"];

// Presets de denumire/model per categorie PSA — user selectează din listă în loc să tasteze.
// Lista poate fi extinsă în timp. Opțiunea „__custom__" permite introducere manuală.
const PSA_NAME_PRESETS = {
  masca: [
    "3M Vollmaske 6800",
    "3M Vollmaske 7800",
    "3M Vollmaske 6900",
    "Sundström SR100",
    "Sundström SR200",
    "Sundström SR-Half",
  ],
  furtun: [
    "Furtun 3M BT-30",
    "Furtun 3M BT-40",
    "Furtun Sundström SR358",
    "Furtun Sundström SR550",
    "Furtun universal cu cuplaj rapid",
  ],
  curea: [
    "3M Versaflo TR-300+",
    "3M Versaflo TR-600",
    "3M Adflo PAPR",
    "Sundström SR500",
    "Sundström SR700",
  ],
  casca: [
    "Cască protecție albă",
    "Cască protecție galbenă",
    "Cască protecție portocalie",
    "Cască protecție roșie",
    "Cască 3M G3000",
    "Cască Uvex Pheos",
    "Cască JSP EVO3",
    "Cască cu vizieră integrată",
  ],
  cizme: [
    "Cizme cauciuc protecție S5",
    "Cizme cauciuc cu vârf metalic",
    "Cizme termoizolante",
    "Cizme PVC scurte",
  ],
  hanorac: [
    "Hanorac portocaliu cu glugă",
  ],
  tricou: [
    "ConVic Portocaliu",
  ],
  geaca: [
    "Geacă ConVic iarnă",
    "Geacă softshell",
    "Geacă reflectorizantă",
    "Geacă impermeabilă",
  ],
  bocanci: [
    "Bocanci S3 protecție",
    "Bocanci impermeabili",
    "Bocanci iarnă căptușiți",
  ],
  pantaloni: [
    "Pantaloni lucru ConVic",
    "Pantaloni cargo",
    "Pantaloni izolați iarnă",
    "Pantaloni reflectorizanți",
  ],
  altele: [],
};

// Categoriile incluse într-un „Echipament complet"
const COMPLETE_SET_CATEGORIES = ["masca", "curea", "furtun"];

// ---------- Add/Edit/Move dialog ----------
export default function ItemDialog({ mode, item, prefill, meta, stores, cars, onClose, onSaved }) {
  const isMove = mode === "move";
  const { isAdmin } = useAuth();
  const [persons, setPersons] = useState([]);
  const [needSerialPrompt, setNeedSerialPrompt] = useState(false);
  const [promptSerial, setPromptSerial] = useState("");
  const [promptNoSerial, setPromptNoSerial] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Pentru PSA: dacă user alege „Altele" din lista de presets → arătăm input liber
  const [customNameMode, setCustomNameMode] = useState(false);

  const handleDelete = async () => {
    if (!item?.id) return;
    const itemName = item.name || "acest produs";
    const confirmMsg = `Ștergi definitiv „${itemName}"?\n\nAceastă acțiune nu poate fi anulată. Istoricul echipamentului rămâne în jurnalul de audit.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await api.delete(`/inventory/${item.id}`);
      toast.success(`„${itemName}" șters definitiv`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setDeleting(false);
    }
  };
  useEffect(() => {
    if (isMove) {
      api.get("/persons").then((r) => {
        const list = (r.data || []).filter((p) => p.active !== false);
        list.sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`, "ro"));
        setPersons(list);
      }).catch(() => setPersons([]));
    }
  }, [isMove]);
  const [form, setForm] = useState(() => {
    if (item) {
      return {
        name: item.name || "", item_type: item.item_type || "echipament",
        location: item.location || "", color: item.color || "",
        shelf_col: item.shelf_col || "", shelf_row: item.shelf_row || "",
        status: item.status || "functional",
        quantity: item.quantity ?? 1, unit: item.unit || "",
        warranty_until: item.warranty_until || "", purchase_date: item.purchase_date || "",
        store_id: item.store_id || "", linked_car_id: item.linked_car_id || "",
        serial: item.serial || "",
        no_serial: !!item.no_serial,
        note: item.note || "",
        assignee_person_id: item.assignee_person_id || "",
        assigned_at: item.assigned_at ? item.assigned_at.slice(0, 10) : "",
        recipient_car_id: item.recipient_car_id || "",
        recipient_note: item.recipient_note || "",
        reason: "",
        psa_category: item.psa_category || "",
        psa_size: item.psa_size || "",
        price_chf: item.price_chf ?? null,
      };
    }
    return { ...EMPTY_ITEM, ...(prefill || {}), reason: "" };
  });

  // Categorii PSA derivate din meta (built-in + custom)
  const psaCategories = useMemo(() => getPsaCategories(meta), [meta]);

  // Resetează modul custom când se schimbă categoria PSA (preseturile diferă)
  useEffect(() => {
    setCustomNameMode(false);
  }, [form.psa_category, form.item_type]);

  // Lista furnizorilor existenți pentru Select-ul de Furnizor (autocomplete)
  const [suppliers, setSuppliers] = useState([]);
  const [supplierCustomMode, setSupplierCustomMode] = useState(false);
  useEffect(() => {
    if (isMove) return;
    api.get("/inventory/suppliers")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.suppliers || []);
        // Normalizează la array de stringuri (numele furnizorilor)
        const names = list.map((s) => typeof s === "string" ? s : (s?.name || "")).filter(Boolean);
        setSuppliers(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setSuppliers([]));
  }, [isMove]);

  // Denumiri dinamice pentru PSA — distinct names per categorie din inventarul existent
  // (orice produs introdus manual prin „Altele" devine sugestie pentru data viitoare)
  const [dynamicPsaNames, setDynamicPsaNames] = useState({}); // { cat: [name, ...] }
  // Modele persistate per-categorie (salvate explicit prin POST /psa-model-presets)
  const [serverPsaPresets, setServerPsaPresets] = useState({}); // { cat: [name, ...] }
  useEffect(() => {
    if (isMove) return;
    api.get("/inventory/psa-catalog")
      .then((r) => {
        const ibc = r.data?.items_by_category || {};
        const map = {};
        for (const [cat, items] of Object.entries(ibc)) {
          const names = new Set();
          for (const it of items || []) {
            const n = (it.name || "").trim();
            if (n) names.add(n);
          }
          map[cat] = Array.from(names).sort((a, b) => a.localeCompare(b));
        }
        setDynamicPsaNames(map);
      })
      .catch(() => setDynamicPsaNames({}));
    api.get("/inventory/psa-model-presets")
      .then((r) => setServerPsaPresets(r.data || {}))
      .catch(() => setServerPsaPresets({}));
  }, [isMove]);

  // Auto-prefill model name când utilizatorul alege o categorie PSA și numele este gol
  // (folosește primul preset disponibil: built-in sau persistat)
  useEffect(() => {
    if (item || isMove) return;
    if (form.item_type !== "psa" || !form.psa_category || form.psa_category === "echipament_complet") return;
    if (form.name && form.name.trim() !== "") return; // nu suprascrie ce a tastat user-ul
    const builtin = PSA_NAME_PRESETS[form.psa_category] || [];
    const persisted = serverPsaPresets[form.psa_category] || [];
    const first = builtin[0] || persisted[0];
    if (first) {
      setForm((f) => ({ ...f, name: first }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.psa_category, form.item_type, serverPsaPresets]);

  const isShelfLoc = meta.shelf_locations.includes(form.location);

  const submit = async () => {
    if (!form.name && !isMove) { toast.error("Denumire obligatorie"); return; }
    // La mutare: dacă item-ul nu are nici SN nici no_serial, blochează și afișează prompt obligatoriu
    if (isMove && item && !item.serial && !item.no_serial) {
      setNeedSerialPrompt(true);
      return;
    }
    // La predare către o persoană (asignare), data este obligatorie
    if (isMove && form.assignee_person_id && form.assignee_person_id !== "__clear__" && !form.assigned_at) {
      toast.error("Specifică data predării către angajat");
      return;
    }
    const payload = {
      ...form,
      shelf_col: form.shelf_col ? parseInt(form.shelf_col) : null,
      shelf_row: form.shelf_row || null,
      quantity: parseFloat(form.quantity || 0) || 0,
      store_id: form.store_id || null,
      linked_car_id: form.linked_car_id || null,
    };
    delete payload.reason;
    try {
      if (isMove) {
        const movePayload = {
          location: payload.location || null,
          color: payload.color || null,
          shelf_col: payload.shelf_col, shelf_row: payload.shelf_row,
          new_status: form.status, reason: form.reason,
        };
        if (form.assignee_person_id === "__clear__") {
          movePayload.assignee_clear = true;
        } else if (form.assignee_person_id) {
          movePayload.assignee_person_id = form.assignee_person_id;
        }
        if (form.recipient_car_id && form.recipient_car_id !== "__none__") {
          movePayload.recipient_car_id = form.recipient_car_id;
        } else {
          movePayload.recipient_car_id = null;
        }
        if (form.recipient_note != null) movePayload.recipient_note = form.recipient_note || null;
        // Propagă data manuală a operațiunii (dacă userul a setat-o)
        if (form.assigned_at) movePayload.operation_date = form.assigned_at;
        await api.post(`/inventory/${item.id}/move`, movePayload);
        toast.success(form.assignee_person_id && form.assignee_person_id !== "__clear__" ? "Mutat și asignat" : "Mutat");
      } else if (item) {
        // Update item existent: includem și assigned_at dacă userul l-a modificat
        if (form.assigned_at && form.assigned_at !== (item.assigned_at ? item.assigned_at.slice(0, 10) : "")) {
          payload.assigned_at = form.assigned_at;
        } else if (!form.assigned_at && item.assigned_at) {
          payload.assigned_at = null;
        }
        await api.put(`/inventory/${item.id}`, payload);
        toast.success("Actualizat");
      } else if (form.item_type === "psa" && form.psa_category === "echipament_complet") {
        // Echipament complet: creează N seturi × 3 items (mască, curea, furtun) fiecare
        const setCount = Math.max(1, parseInt(form.quantity || 1));
        const base = { ...payload };
        delete base.psa_sets;
        delete base.psa_serials;
        delete base.psa_category;
        delete base.psa_size;
        delete base.serial;
        delete base.no_serial;
        delete base.quantity;
        const labelMap = { masca: "Mască", curea: "Curea", furtun: "Furtun" };
        let created = 0;
        for (let i = 0; i < setCount; i++) {
          for (const subCat of ["masca", "curea", "furtun"]) {
            const subEntry = (form.psa_sets?.[i]?.[subCat]) || { serial: "", no_serial: false };
            // Numele preferă seria reală a sub-itemului; fallback la „(set i+1)" pentru identificare
            let suffix = "";
            if (subEntry.serial && subEntry.serial.trim()) {
              suffix = ` ${subEntry.serial.trim()}`;
            } else if (setCount > 1) {
              suffix = ` (set ${i + 1})`;
            }
            const subPayload = {
              ...base,
              name: `${form.name} · ${labelMap[subCat]}${suffix}`,
              psa_category: subCat,
              quantity: 1,
              serial: subEntry.serial || "",
              no_serial: !!subEntry.no_serial,
            };
            await api.post("/inventory", subPayload);
            created++;
          }
        }
        toast.success(`Adăugat: ${created} items (${setCount} set${setCount > 1 ? "uri" : ""} complete)`);
      } else if (
        form.item_type === "psa" &&
        parseInt(form.quantity || 1) > 1 &&
        psaCategories.find((c) => c.code === form.psa_category)?.is_respiratory
      ) {
        // PSA respiratoriu (mască/furtun/curea) cu cantitate > 1: creează N items separați cu serii individuale
        const count = parseInt(form.quantity);
        const base = { ...payload };
        delete base.psa_serials;
        delete base.quantity;
        delete base.serial;
        delete base.no_serial;
        let created = 0;
        for (let i = 0; i < count; i++) {
          const entry = (form.psa_serials?.[i]) || { serial: "", no_serial: false };
          // Numele păstrează modelul + (dacă există) seria reală introdusă de user (ex: „Sundström SR100 77")
          // Pentru intrări fără serie (no_serial = true) folosim totuși #i+1 ca fallback ca să fie distinct
          let suffix = "";
          if (entry.serial && entry.serial.trim()) {
            suffix = ` ${entry.serial.trim()}`;
          } else if (count > 1) {
            suffix = ` #${i + 1}`;
          }
          const subPayload = {
            ...base,
            name: `${form.name}${suffix}`,
            quantity: 1,
            serial: entry.serial || "",
            no_serial: !!entry.no_serial,
          };
          await api.post("/inventory", subPayload);
          created++;
        }
        toast.success(`Adăugat: ${created} buc.`);
      } else if (form.item_type === "psa") {
        // PSA cu mărime (tricou/cizme/hanorac/geacă/bocanci/pantaloni) sau orice altă categorie non-respiratorie:
        // un singur item cu quantity (FĂRĂ nr de serie — produsele identice se identifică prin mărime + model)
        const cleanPayload = { ...payload };
        delete cleanPayload.psa_serials;
        delete cleanPayload.psa_sets;
        // Pentru PSA non-respiratorii forțăm „no_serial=true" ca să sară peste validarea backend
        const isRespiratory = psaCategories.find((c) => c.code === form.psa_category)?.is_respiratory;
        if (!isRespiratory) {
          cleanPayload.serial = "";
          cleanPayload.no_serial = true;
        }
        await api.post("/inventory", cleanPayload);
        toast.success("Adăugat");
      } else {
        // Default (echipament/consumabil/piesa_auto)
        const cleanPayload = { ...payload };
        delete cleanPayload.psa_serials;
        delete cleanPayload.psa_sets;
        await api.post("/inventory", cleanPayload);
        toast.success("Adăugat");
      }
      // Persistă modelul în lista de presets (dacă PSA și nume custom care nu e în baseline)
      try {
        if (!item && form.item_type === "psa" && form.psa_category && form.psa_category !== "echipament_complet" && form.name) {
          const baseline = PSA_NAME_PRESETS[form.psa_category] || [];
          const trimmedName = form.name.trim();
          if (trimmedName && !baseline.includes(trimmedName)) {
            await api.post("/inventory/psa-model-presets", {
              psa_category: form.psa_category,
              model: trimmedName,
            }).catch(() => {});
          }
        }
      } catch { /* non-blocking */ }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isMove
              ? `Mută: ${item?.name}`
              : (item
                  ? "Editează echipament"
                  : (prefill?.location && prefill?.shelf_row
                      ? `Plasare rapidă · ${prefill.location} · ${prefill.shelf_row}${prefill.shelf_col}`
                      : "Echipament nou"))}
          </DialogTitle>
        </DialogHeader>

        {/* === Banner prominent „REPARAȚII" pentru itemi existenți === */}
        {item?.id && !isMove && (
          <ItemRepairsTopBanner item={item} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {!isMove && (
            <>
              {/* === PAS 1: Tip echipament (card-uri vizuale) — la TOP === */}
              {!item && (
                <div className="md:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                    Pas 1 · Ce tip de echipament adaugi?
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {ITEM_TYPES.map((t) => {
                      const metaT = ITEM_TYPE_META[t.code] || { icon: "📦", label: t.label, desc: "", color: "zinc" };
                      const active = form.item_type === t.code;
                      const colorMap = {
                        blue:    { active: "border-blue-500 bg-blue-50",       text: "text-blue-900" },
                        cyan:    { active: "border-cyan-500 bg-cyan-50",       text: "text-cyan-900" },
                        amber:   { active: "border-amber-500 bg-amber-50",     text: "text-amber-900" },
                        emerald: { active: "border-emerald-500 bg-emerald-50", text: "text-emerald-900" },
                        zinc:    { active: "border-zinc-500 bg-zinc-50",       text: "text-zinc-900" },
                      };
                      const c = colorMap[metaT.color] || colorMap.zinc;
                      return (
                        <button
                          key={t.code}
                          type="button"
                          onClick={() => setForm({ ...form, item_type: t.code, psa_category: "", psa_size: "", supplier: form.supplier })}
                          className={`text-left p-3 rounded-lg border-2 transition-all ${active ? `${c.active} ${c.text} shadow-sm` : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                          data-testid={`item-type-card-${t.code}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">{metaT.icon}</span>
                            <span className="text-sm font-bold">{metaT.label}</span>
                          </div>
                          <div className="text-[10px] text-zinc-500 leading-tight">{metaT.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* === PAS 2: Categorie PSA (cards) — DOAR pentru PSA === */}
              {!item && form.item_type === "psa" && (
                <div className="md:col-span-2 p-3 rounded-md bg-cyan-50 border border-cyan-300">
                  <Label className="text-xs font-bold uppercase tracking-wider text-cyan-900">
                    Pas 2 · Ce categorie de PSA?
                  </Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5 mt-2" data-testid="psa-category-cards">
                    {psaCategories.map((c) => {
                      const active = form.psa_category === c.code;
                      return (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => setForm({ ...form, psa_category: c.code, psa_size: c.needs_size ? form.psa_size : "" })}
                          className={`flex flex-col items-center gap-1 p-2 rounded border-2 transition-all ${active ? "border-cyan-600 bg-white shadow-sm" : "border-cyan-200 bg-white/60 hover:border-cyan-400"}`}
                          data-testid={`psa-category-card-${c.code}`}
                          title={c.label}
                        >
                          {c.code === "echipament_complet" ? (
                            <span className="text-2xl">🛡</span>
                          ) : (
                            <PSACategoryIcon cat={c.code} size={28} className={active ? "text-cyan-700" : "text-zinc-500"}/>
                          )}
                          <span className={`text-[10px] font-semibold text-center leading-tight ${active ? "text-cyan-900" : "text-zinc-700"}`}>{c.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* === PAS 3: Detalii produs — vizibile doar după ce tipul (și categoria PSA dacă e cazul) sunt alese === */}
              {(item || form.item_type !== "psa" || form.psa_category) && (
                <>
                  <div className="md:col-span-2 pt-1 border-t border-zinc-100">
                    {!item && (
                      <div className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2">
                        Pas 3 · Detalii produs
                      </div>
                    )}
                  </div>

                  {/* Denumire — Select cu presets pentru PSA NOU; la editare, input liber */}
                  {form.item_type === "psa" && form.psa_category && form.psa_category !== "echipament_complet" && !item ? (
                    <div className="md:col-span-2">
                      <Label>Denumire / Model produs</Label>
                      {(() => {
                        const baseline = PSA_NAME_PRESETS[form.psa_category] || [];
                        const persisted = serverPsaPresets[form.psa_category] || [];
                        const dynamic = dynamicPsaNames[form.psa_category] || [];
                        // Merge: baseline first, then persisted (server), then dynamic (inventar), alfabetic în categoriile auxiliare
                        const merged = [
                          ...baseline,
                          ...persisted.filter((n) => !baseline.includes(n)),
                          ...dynamic.filter((n) => !baseline.includes(n) && !persisted.includes(n)).sort((a, b) => a.localeCompare(b)),
                        ];
                        const isPreset = merged.includes(form.name);
                        const selectValue = customNameMode ? "__custom__" : (isPreset ? form.name : (form.name ? "__custom__" : ""));
                        return (
                          <>
                            <Select
                              value={selectValue || undefined}
                              onValueChange={(v) => {
                                if (v === "__custom__") {
                                  setCustomNameMode(true);
                                  setForm({ ...form, name: "" });
                                } else {
                                  setCustomNameMode(false);
                                  setForm({ ...form, name: v });
                                }
                              }}
                            >
                              <SelectTrigger data-testid="item-name-select"><SelectValue placeholder="— Alege modelul —" /></SelectTrigger>
                              <SelectContent>
                                {merged.map((p) => (
                                  <SelectItem key={p} value={p}>{p}</SelectItem>
                                ))}
                                <SelectItem value="__custom__">✏ Altele — introdu manual</SelectItem>
                              </SelectContent>
                            </Select>
                            {(customNameMode || (form.name && !isPreset)) && (
                              <Input
                                className="mt-2"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Denumire personalizată — va apărea ca sugestie data viitoare"
                                data-testid="item-name-custom"
                                autoFocus
                              />
                            )}
                            {merged.length === 0 && !customNameMode && (
                              <div className="text-[11px] text-zinc-500 mt-1 italic">
                                Nu există presets pentru această categorie — apasă „Altele" pentru a introduce manual.
                              </div>
                            )}
                            {dynamic.length > 0 && (
                              <div className="text-[10px] text-zinc-500 mt-1 italic">
                                Lista include {dynamic.length} {dynamic.length === 1 ? "denumire" : "denumiri"} preluate din inventarul existent.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="md:col-span-2">
                      <Label>Denumire / Model produs</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={
                          form.item_type === "psa" && form.psa_category === "echipament_complet" ? "ex: Set 3M Versaflo complet" :
                          form.item_type === "consumabil" ? "ex: Ulei motor 5W30" :
                          "ex: Bormașină Hilti TE 30"
                        }
                        data-testid="item-name"
                      />
                    </div>
                  )}

                  {/* Equipment Category — pentru toate item_type, dar mai relevant pentru echipament */}
                  {form.item_type !== "psa" && (
                    <div className="md:col-span-2">
                      <Label className="text-xs">Categorie echipament (pentru grupare)</Label>
                      <Input
                        list="equipment-category-suggestions"
                        value={form.equipment_category || ""}
                        onChange={(e) => setForm({ ...form, equipment_category: e.target.value })}
                        placeholder="ex: Airless, Mașină de șlefuit, Messschreiber, Aspirator..."
                        data-testid="item-equipment-category"
                      />
                      <datalist id="equipment-category-suggestions">
                        {(meta?.equipment_categories && meta.equipment_categories.length > 0
                          ? meta.equipment_categories
                          : [
                              "Airless", "Mașină de șlefuit", "Messschreiber", "Aspirator",
                              "Pistol vopsit", "Compresor", "Generator", "Ferăstrău",
                              "Mașină găurit", "Polizor unghiular", "Lampă lucru",
                              "Scară", "Schele", "Bormașină", "Înșurubelniță electrică",
                            ]
                        ).map((s) => (
                          <option key={s} value={s} />
                        ))}
                      </datalist>
                      <div className="text-[10px] text-zinc-500 mt-1 italic">
                        Grupează echipamentele similare în Inventar (ex: toate airless-urile împreună). Opțional.
                      </div>
                    </div>
                  )}

                  {/* Model 3M/Sundström — DOAR pentru PSA respiratorii (mască/furtun/curea) */}
                  {form.item_type === "psa" && psaCategories.find((c) => c.code === form.psa_category)?.is_respiratory && form.psa_category !== "echipament_complet" && (
                    <div className="md:col-span-2">
                      <Label className="text-xs">Model echipament respirator</Label>
                      <div className="flex gap-2 flex-wrap mt-1">
                        {RESPIRATORY_MODELS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setForm({ ...form, supplier: m === "Altele" ? "" : m })}
                            className={`text-xs px-3 py-1.5 rounded border-2 font-semibold transition-colors ${form.supplier === m ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-800 border-blue-300 hover:border-blue-500"}`}
                            data-testid={`item-respiratory-model-${m}`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cantitate + UM */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Cantitate {form.item_type === "psa" ? "(bucăți)" : ""}</Label>
                      <Input type="number" step={form.item_type === "psa" ? "1" : "0.01"} min={form.item_type === "psa" ? "1" : "0"} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="item-quantity" />
                    </div>
                    <div>
                      <Label>UM</Label>
                      <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder={form.item_type === "psa" ? "buc" : "buc, L..."} />
                    </div>
                  </div>

                  {/* Preț */}
                  <div>
                    <Label>💰 Preț unitar (CHF, opțional)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.price_chf ?? ""}
                      onChange={(e) => setForm({ ...form, price_chf: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      placeholder="ex: 89.50"
                      data-testid="item-price-chf"
                    />
                  </div>

                  {/* Mărime — DOAR pentru PSA cu needs_size (tricou, cizme, hanorac, etc.) */}
                  {form.item_type === "psa" && psaCategories.find((c) => c.code === form.psa_category)?.needs_size && (
                    <div className="md:col-span-2">
                      <Label className="text-xs">Mărime</Label>
                      {(() => {
                        // Mărimi predefinite per categorie
                        const SIZE_PRESETS = {
                          masca:    ["S", "M", "L", "XL"],
                          tricou:   ["XS", "S", "M", "L", "XL", "XXL"],
                          hanorac:  ["XS", "S", "M", "L", "XL", "XXL"],
                          geaca:    ["XS", "S", "M", "L", "XL", "XXL"],
                          pantaloni:["XS", "S", "M", "L", "XL", "XXL", "46", "48", "50", "52", "54", "56"],
                          cizme:   ["38", "39", "40", "41", "42", "43", "44", "45", "46"],
                          bocanci: ["38", "39", "40", "41", "42", "43", "44", "45", "46"],
                        };
                        const presets = SIZE_PRESETS[form.psa_category];
                        if (presets) {
                          return (
                            <Select
                              value={form.psa_size || undefined}
                              onValueChange={(v) => setForm({ ...form, psa_size: v })}
                            >
                              <SelectTrigger data-testid="psa-size-select"><SelectValue placeholder="— Alege mărimea —" /></SelectTrigger>
                              <SelectContent>
                                {presets.map((s) => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        }
                        // Fallback: input liber pentru alte categorii
                        return (
                          <Input
                            value={form.psa_size || ""}
                            onChange={(e) => setForm({ ...form, psa_size: e.target.value })}
                            placeholder="S, M, L, XL, XXL, 42, 43..."
                            data-testid="psa-size"
                          />
                        );
                      })()}
                    </div>
                  )}

                  {/* Mașină asociată — pentru echipament, piesă auto SAU consumabil (ex: ulei alocat unei mașini anume) */}
                  {(form.item_type === "echipament" || form.item_type === "piesa_auto" || form.item_type === "consumabil") && (
                    <div className="md:col-span-2">
                      <Label>🚗 Mașină asociată (opțional)</Label>
                      <Select value={form.linked_car_id || "none"} onValueChange={(v) => setForm({ ...form, linked_car_id: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="linked-car-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Niciuna (stoc general) —</SelectItem>
                          {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.marca} {c.model} ({c.numar_inmatriculare})</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {form.item_type === "consumabil" && form.linked_car_id && (
                        <p className="text-[11px] text-zinc-500 mt-1">Acest consumabil va fi alocat mașinii selectate și va apărea pe pagina ei în secțiunea Piese & Consumabile.</p>
                      )}
                    </div>
                  )}

                  {/* Data achiziție + Garanție — relevante pentru toate, dar utile mai ales la echipament/piesă */}
                  {(form.item_type === "echipament" || form.item_type === "piesa_auto" || form.item_type === "psa") && (
                    <>
                      <div><Label>Data achiziție</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
                      <div><Label>Garanție până la</Label><Input type="date" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} /></div>
                      <div className="md:col-span-2">
                        <Label className="flex items-center gap-1">
                          📅 Data predării / atribuirii
                          <span className="text-[10px] text-zinc-500 font-normal">(de când e în locația/la persoana de mai jos)</span>
                        </Label>
                        <Input
                          type="date"
                          value={form.assigned_at || ""}
                          onChange={(e) => setForm({ ...form, assigned_at: e.target.value })}
                          max={new Date().toISOString().slice(0, 10)}
                          data-testid="item-assigned-at"
                          placeholder="Lasă gol pentru azi"
                        />
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          Setează retroactiv dacă echipamentul a fost predat în trecut (ex: PSA dat unui angajat în 2024). Dacă lipsește, se folosește data de achiziție sau azi.
                        </div>
                      </div>
                    </>
                  )}

                  {/* Serie — DOAR pentru echipament / piesă auto (PSA are managementul propriu mai jos) */}
                  {(form.item_type === "echipament" || form.item_type === "piesa_auto") && (
                    <div className="md:col-span-2">
                      <Label>Serie</Label>
                      <Input
                        data-testid="item-serial"
                        value={form.serial || ""}
                        onChange={(e) => setForm({ ...form, serial: e.target.value, no_serial: e.target.value ? false : form.no_serial })}
                        placeholder="Serie / număr unic (obligatoriu)"
                        disabled={!!form.no_serial}
                      />
                      <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-xs text-zinc-700">
                        <Checkbox
                          id="no-serial"
                          checked={!!form.no_serial}
                          onCheckedChange={(v) => setForm({ ...form, no_serial: !!v, serial: v ? "" : form.serial })}
                          data-testid="item-no-serial"
                        />
                        <span>Acest echipament <strong>NU are nr de serie</strong> (marcat explicit)</span>
                      </label>
                      {!form.serial && !form.no_serial && (
                        <div className="mt-2 text-[11px] bg-amber-50 border border-amber-300 rounded p-2 text-amber-900" data-testid="item-serial-warning">
                          ⚠️ <strong>Atenție:</strong> Nr. de serie este obligatoriu. Introdu seria sau bifează „NU are nr de serie".
                        </div>
                      )}
                    </div>
                  )}

                  {/* Câmpuri specifice CONSUMABIL */}
                  {form.item_type === "consumabil" && (
                    <>
                      <div className="md:col-span-2 flex items-center gap-2 p-2.5 rounded-md bg-[#FFFBEB] border border-[#FCD34D]">
                        <Checkbox id="is-oil" checked={!!form.is_oil} onCheckedChange={(v) => setForm({ ...form, is_oil: !!v })} data-testid="is-oil-checkbox" />
                        <label htmlFor="is-oil" className="text-sm cursor-pointer flex items-center gap-1.5">
                          <Droplet size={14} className="text-[#D97706]"/>
                          Acest consumabil este <strong>ulei</strong> — va fi inclus în stocul total de ulei
                        </label>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="small-label">🚗 Categorie consumabil mașini (opțional)</Label>
                        <Select
                          value={form.car_consumable_type || "__none__"}
                          onValueChange={(v) => setForm({ ...form, car_consumable_type: v === "__none__" ? null : v, is_oil: v === "ulei_motor" ? true : form.is_oil })}
                        >
                          <SelectTrigger className="mt-1" data-testid="car-consumable-type"><SelectValue placeholder="— Nu este consumabil auto —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nu este consumabil auto —</SelectItem>
                            <SelectItem value="ulei_motor">🛢️ Ulei motor (litri)</SelectItem>
                            <SelectItem value="antigel">❄️ Antigel (litri)</SelectItem>
                            <SelectItem value="lichid_spalator">💧 Lichid spălător (litri)</SelectItem>
                            <SelectItem value="lichid_frana">🛑 Lichid frână (litri)</SelectItem>
                            <SelectItem value="filtru_ulei">🛢️ Filtru ulei (buc)</SelectItem>
                            <SelectItem value="filtru_aer">💨 Filtru aer (buc)</SelectItem>
                            <SelectItem value="filtru_combustibil">⛽ Filtru combustibil (buc)</SelectItem>
                            <SelectItem value="filtru_habitaclu">🧊 Filtru habitaclu (buc)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* === Câmpuri specifice PSA: serii / seturi complete === */}
                  {form.item_type === "psa" && (
                    <div className="md:col-span-2 p-3 rounded-md bg-cyan-50 border border-cyan-300 space-y-2.5" data-testid="psa-fields">

                      {/* Echipament complet — editor seturi multiple */}
                      {form.psa_category === "echipament_complet" && !item && (
                        <div className="p-2.5 rounded bg-white border border-cyan-300 space-y-2" data-testid="psa-complete-set-block">
                          <div className="text-[11px] text-cyan-900 font-semibold">
                            🛡 Vor fi create <strong>{Math.max(1, parseInt(form.quantity || 1)) * 3} items</strong> separați
                            ({Math.max(1, parseInt(form.quantity || 1))} × Mască + {Math.max(1, parseInt(form.quantity || 1))} × Curea + {Math.max(1, parseInt(form.quantity || 1))} × Furtun)
                          </div>
                          <div className="text-[11px] text-zinc-600">
                            Pentru fiecare SET introdu seria fiecărei componente (sau bifează „fără serie").
                          </div>
                          {Array.from({ length: Math.max(1, parseInt(form.quantity || 1)) }).map((_, idx) => (
                            <div key={idx} className="border border-cyan-200 rounded p-2 space-y-1.5 bg-cyan-50/40" data-testid={`psa-set-${idx}`}>
                              <div className="text-[10px] font-bold uppercase text-cyan-800">Set #{idx + 1}</div>
                              {["masca", "curea", "furtun"].map((subCat) => {
                                const subEntry = (form.psa_sets?.[idx]?.[subCat]) || { serial: "", no_serial: false };
                                const labelMap = { masca: "Mască", curea: "Curea", furtun: "Furtun" };
                                return (
                                  <div key={subCat} className="grid grid-cols-[110px_1fr_110px] gap-1.5 items-center">
                                    <span className="inline-flex items-center gap-1 text-[11px] text-zinc-700">
                                      <PSACategoryIcon cat={subCat} size={14} className="text-cyan-700"/>
                                      {labelMap[subCat]}
                                    </span>
                                    <Input
                                      className="h-8 text-xs"
                                      placeholder="Serie..."
                                      value={subEntry.serial}
                                      disabled={subEntry.no_serial}
                                      onChange={(e) => {
                                        const sets = { ...(form.psa_sets || {}) };
                                        sets[idx] = { ...(sets[idx] || {}), [subCat]: { serial: e.target.value, no_serial: false } };
                                        setForm({ ...form, psa_sets: sets });
                                      }}
                                      data-testid={`psa-set-${idx}-${subCat}-serial`}
                                    />
                                    <label className="text-[10px] flex items-center gap-1 cursor-pointer text-zinc-600">
                                      <Checkbox
                                        checked={subEntry.no_serial}
                                        onCheckedChange={(v) => {
                                          const sets = { ...(form.psa_sets || {}) };
                                          sets[idx] = { ...(sets[idx] || {}), [subCat]: { serial: v ? "" : subEntry.serial, no_serial: !!v } };
                                          setForm({ ...form, psa_sets: sets });
                                        }}
                                        data-testid={`psa-set-${idx}-${subCat}-noserial`}
                                      />
                                      <span>fără serie</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Serii individuale pentru cantitate > 1 — doar PSA respiratorii (mască/furtun/curea) */}
                      {form.psa_category !== "echipament_complet" && !item && parseInt(form.quantity || 1) > 1 && psaCategories.find((c) => c.code === form.psa_category)?.is_respiratory && (
                        <div className="p-2.5 rounded bg-white border border-cyan-300 space-y-2" data-testid="psa-multi-serials-block">
                          <div className="text-[11px] text-cyan-900 font-semibold">
                            🔢 Vor fi create <strong>{parseInt(form.quantity)} items</strong> separați — introdu seria pentru fiecare:
                          </div>
                          {Array.from({ length: parseInt(form.quantity || 1) }).map((_, idx) => {
                            const entry = (form.psa_serials?.[idx]) || { serial: "", no_serial: false };
                            return (
                              <div key={idx} className="grid grid-cols-[60px_1fr_110px] gap-2 items-center" data-testid={`psa-buc-${idx}`}>
                                <span className="text-[11px] font-mono text-zinc-700">#{idx + 1}</span>
                                <Input
                                  className="h-8 text-xs"
                                  placeholder={`Serie buc. ${idx + 1}...`}
                                  value={entry.serial}
                                  disabled={entry.no_serial}
                                  onChange={(e) => {
                                    const sers = { ...(form.psa_serials || {}) };
                                    sers[idx] = { serial: e.target.value, no_serial: false };
                                    setForm({ ...form, psa_serials: sers });
                                  }}
                                  data-testid={`psa-serial-${idx}`}
                                />
                                <label className="text-[10px] flex items-center gap-1 cursor-pointer text-zinc-600">
                                  <Checkbox
                                    checked={entry.no_serial}
                                    onCheckedChange={(v) => {
                                      const sers = { ...(form.psa_serials || {}) };
                                      sers[idx] = { serial: v ? "" : entry.serial, no_serial: !!v };
                                      setForm({ ...form, psa_serials: sers });
                                    }}
                                    data-testid={`psa-noserial-${idx}`}
                                  />
                                  <span>fără serie</span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Serie pentru cantitate = 1 — DOAR pentru PSA respiratorii (mască/furtun/curea) */}
                      {form.psa_category !== "echipament_complet" && !item && parseInt(form.quantity || 1) <= 1 && psaCategories.find((c) => c.code === form.psa_category)?.is_respiratory && (
                        <div className="p-2.5 rounded bg-white border border-cyan-300">
                          <Label className="text-xs">Serie produs</Label>
                          <Input
                            className="mt-1"
                            value={form.serial || ""}
                            onChange={(e) => setForm({ ...form, serial: e.target.value, no_serial: e.target.value ? false : form.no_serial })}
                            placeholder="Serie / număr unic"
                            disabled={!!form.no_serial}
                            data-testid="psa-single-serial"
                          />
                          <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-xs text-zinc-700">
                            <Checkbox
                              checked={!!form.no_serial}
                              onCheckedChange={(v) => setForm({ ...form, no_serial: !!v, serial: v ? "" : form.serial })}
                              data-testid="psa-single-noserial"
                            />
                            <span>Acest PSA <strong>NU are nr de serie</strong></span>
                          </label>
                        </div>
                      )}

                      {/* Hint pentru PSA cu mărime (tricou/cizme/etc.) — fără serie */}
                      {psaCategories.find((c) => c.code === form.psa_category)?.needs_size && (
                        <div className="text-[11px] text-zinc-600 italic">
                          ℹ Pentru această categorie de PSA nu se cere nr. de serie — produsele sunt identificate prin mărime și model.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className="md:col-span-2 pt-2 border-t border-[var(--border)]">
            <div className="small-label">{isMove ? "Mutare către" : "Locație și culoare"}</div>
            {isMove && (
              <div className="text-[11px] text-zinc-600 bg-amber-50 border border-amber-200 rounded p-2 mt-1.5">
                ℹ️ <strong>Reguli:</strong> Sculele „colorate" aparțin permanent unui set (ex: 🔴 Roșu) — culoarea rămâne mereu. Locația arată unde sunt FIZIC acum:<br/>
                • <strong>MAGAZIE/PAXLI/etc.</strong> = stocat (trebuie raft) · <strong>Fără locație</strong> = pe șantier sau în mașină (selectează „🚗 mașina" mai jos).
              </div>
            )}
          </div>
          <div>
            <Label>Locație fizică</Label>
            <Select value={form.location || "none"} onValueChange={(v) => setForm({ ...form, location: v === "none" ? "" : v, shelf_col: "", shelf_row: "" })}>
              <SelectTrigger data-testid="item-location"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Fără locație (pe drum / la mașină) —</SelectItem>
                {meta.locations.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Culoare set — DOAR pentru echipament/sculă (irelevant pentru PSA/consumabil/piesă auto) */}
          {(isMove || form.item_type === "echipament") && (
            <div>
              <Label>Culoare set (permanent)</Label>
              <Select value={form.color || "none"} onValueChange={(v) => setForm({ ...form, color: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="item-color"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Fără set (sculă liberă) —</SelectItem>
                  {meta.colors.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isMove && form.color && form.location && !isShelfLoc && (
            <div className="md:col-span-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 -mt-1">
              ⚠️ Această locație ({form.location}) nu acceptă rafturi. Dacă scula e depozitată, recomandat MAGAZIE/PAXLI/ACASA VICTOR.
            </div>
          )}
          {isMove && form.color && !form.location && (
            <div className="md:col-span-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-300 rounded p-2 -mt-1">
              🚗 Scula e marcată ca „pe drum" — selectează mașina de mai jos pentru a fi vizibilă în CarDetail.
            </div>
          )}

          {isShelfLoc && (
            <>
              <div>
                <Label>Raft (rând A–J)</Label>
                <Select value={form.shelf_row || "none"} onValueChange={(v) => setForm({ ...form, shelf_row: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="item-shelf-row"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {meta.rows.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Coloană (1–{meta.max_col})</Label>
                <Input type="number" min={1} max={meta.max_col} value={form.shelf_col} onChange={(e) => setForm({ ...form, shelf_col: e.target.value })} data-testid="item-shelf-col" />
              </div>
            </>
          )}

          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="item-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="functional"><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--green-valid)]"/>Funcțional</span></SelectItem>
                <SelectItem value="defect"><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--red-bila)]"/>Defect</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isMove && (
            <div className="md:col-span-2 p-3 rounded-md bg-blue-50 border border-blue-200" data-testid="move-to-person-block">
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">👤 Asignează către o persoană (opțional)</Label>
              <p className="text-[11px] text-zinc-600 mb-2 mt-0.5">
                Echipamentul rămâne în locația de mai sus, dar va fi marcat ca <strong>preluat de</strong> persoana selectată.
              </p>
              <Select
                value={form.assignee_person_id || "none"}
                onValueChange={(v) => setForm({ ...form, assignee_person_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="item-move-to-person"><SelectValue placeholder="— Fără asignare —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Fără asignare (păstrează cea curentă) —</SelectItem>
                  {item?.assignee && (
                    <SelectItem value="__clear__" data-testid="item-move-clear-assignee">
                      🗑 Scoate asignarea curentă ({item.assignee})
                    </SelectItem>
                  )}
                  {persons.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nume} {p.prenume}
                      {p.firma_id ? "" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {item?.assignee && !form.assignee_person_id && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
                  ⚠️ Acum asignat: <strong>{item.assignee}</strong>. Lasă „Fără asignare" pentru a-l păstra.
                </div>
              )}
              {/* Data manuală a predării / mutării — permite înregistrare retroactivă */}
              <div className="mt-3 pt-2 border-t border-blue-200">
                <Label className="text-[11px] uppercase tracking-wider text-blue-900 font-bold flex items-center gap-1">
                  📅 Data predării / mutării
                  {form.assignee_person_id && form.assignee_person_id !== "__clear__" && (
                    <span className="text-red-600">*</span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={form.assigned_at || ""}
                  onChange={(e) => setForm({ ...form, assigned_at: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  required={!!form.assignee_person_id && form.assignee_person_id !== "__clear__"}
                  className="mt-1 h-9"
                  data-testid="item-move-operation-date"
                />
                <div className="text-[10px] text-blue-700 mt-1">
                  {form.assignee_person_id && form.assignee_person_id !== "__clear__"
                    ? "Obligatoriu la predarea către o persoană. Default azi — modifică pentru o predare retroactivă."
                    : "Lasă gol pentru azi. Selectează o dată din trecut pentru a înregistra retroactiv o predare deja făcută."}
                </div>
              </div>
            </div>
          )}

          {isMove && (
            <div className="md:col-span-2 p-3 rounded-md bg-emerald-50 border border-emerald-200" data-testid="move-to-car-block">
              <Label className="text-xs font-bold uppercase tracking-wider text-emerald-900">🚗 Către ce mașină (opțional)</Label>
              <p className="text-[11px] text-zinc-600 mb-2 mt-0.5">
                Selectează mașina la care merge sculele. Util când le aloci la o culoare (SET ROȘU etc.) și vrei să se vadă în CarDetail.
              </p>
              <Select
                value={form.recipient_car_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, recipient_car_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="item-move-to-car"><SelectValue placeholder="— Nicio mașină —"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nicio mașină —</SelectItem>
                  {(cars || []).map((c) => {
                    const COLOR_EMOJI = { rosu: "🔴", galben: "🟡", albastru: "🔵", verde: "🟢", gri: "⚪", mov: "🟣" };
                    const emoji = c.tool_set_color ? COLOR_EMOJI[c.tool_set_color] || "🎨" : "";
                    const label = `${c.marca || ""} ${c.model || ""}`.trim();
                    return (
                      <SelectItem key={c.id} value={c.id}>
                        {emoji && <span className="mr-1">{emoji}</span>}{label || c.id} {c.numar_inmatriculare ? `(${c.numar_inmatriculare})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Label className="text-xs mt-2 block">Notă destinatar (opțional)</Label>
              <Input
                value={form.recipient_note}
                onChange={(e) => setForm({ ...form, recipient_note: e.target.value })}
                placeholder="ex: predat lui Pop Ion personal · sau: lăsat în portbagaj"
                className="mt-1"
                data-testid="item-move-recipient-note"
              />
            </div>
          )}

          {isMove && (
            <div className="md:col-span-2"><Label>Motiv mutare</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="ex: trimis la șantier X" /></div>
          )}

          {!isMove && (
            <div className="md:col-span-2">
              <Label>Furnizor</Label>
              {(() => {
                const supVal = form.supplier || "";
                const isKnown = supVal && suppliers.includes(supVal);
                const selectValue = supplierCustomMode ? "__custom__" : (isKnown ? supVal : (supVal ? "__custom__" : ""));
                return (
                  <>
                    <Select
                      value={selectValue || undefined}
                      onValueChange={(v) => {
                        if (v === "__custom__") {
                          setSupplierCustomMode(true);
                          setForm({ ...form, supplier: "" });
                        } else if (v === "__none__") {
                          setSupplierCustomMode(false);
                          setForm({ ...form, supplier: "" });
                        } else {
                          setSupplierCustomMode(false);
                          setForm({ ...form, supplier: v });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="item-supplier-select"><SelectValue placeholder="— Alege furnizorul —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Fără furnizor —</SelectItem>
                        {suppliers.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">➕ Adaugă furnizor nou</SelectItem>
                      </SelectContent>
                    </Select>
                    {(supplierCustomMode || (supVal && !isKnown)) && (
                      <Input
                        className="mt-2"
                        value={form.supplier || ""}
                        onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                        placeholder="Numele noului furnizor (ex: Hilti, Bosch, Mindfit...)"
                        data-testid="item-supplier"
                        autoFocus={supplierCustomMode}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {!isMove && (
            <div className="md:col-span-2"><Label>Notă</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          )}

          {/* === ISTORIC: vizibil doar pentru itemi existenți (edit mode), NU în modul mutare === */}
          {item?.id && !isMove && (
            <div className="md:col-span-2 mt-2 pt-3 border-t border-zinc-200" data-testid="item-detail-history">
              <ItemHistorySection item={item}/>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap sm:justify-between">
          {/* Buton Șterge — doar admin, doar pentru itemi existenți, NU în modul mutare */}
          {isAdmin && item?.id && !isMove ? (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 hover:bg-red-50 border-red-300 mr-auto"
              data-testid="delete-item-btn"
              title="Șterge definitiv acest produs din inventar"
            >
              <Trash2 size={14} className="mr-1"/>
              {deleting ? "Se șterge..." : "🗑 Șterge produs"}
            </Button>
          ) : <span className="mr-auto"/>}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Anulează</Button>
            <Button onClick={submit} className="bg-[var(--primary)]" data-testid="submit-item">{isMove ? "Mută" : (item ? "Salvează" : "Creează")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Prompt obligatoriu: completează nr. serie înainte de mutare */}
      <Dialog open={needSerialPrompt} onOpenChange={(v) => !v && !promptSaving && setNeedSerialPrompt(false)}>
        <DialogContent className="max-w-md" data-testid="serial-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Completează nr. de serie
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-amber-50 border border-amber-300 rounded p-2.5 text-amber-900">
              Înainte de a muta <strong>{item?.name}</strong>, trebuie să completezi nr. de serie sau să marchezi explicit că nu are.
            </div>
            <div>
              <Label className="text-xs">Nr. de serie</Label>
              <Input
                autoFocus
                value={promptSerial}
                onChange={(e) => { setPromptSerial(e.target.value); if (e.target.value) setPromptNoSerial(false); }}
                placeholder="ex: SN-2026-0001"
                disabled={promptNoSerial || promptSaving}
                data-testid="serial-prompt-input"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={promptNoSerial}
                onCheckedChange={(v) => { setPromptNoSerial(!!v); if (v) setPromptSerial(""); }}
                data-testid="serial-prompt-no-serial"
                disabled={promptSaving}
              />
              <span>Acest echipament <strong>NU are nr de serie</strong></span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNeedSerialPrompt(false)} disabled={promptSaving}>Anulează</Button>
            <Button
              onClick={async () => {
                if (!promptSerial.trim() && !promptNoSerial) { toast.error('Introdu seria sau bifează „NU are nr de serie".'); return; }
                setPromptSaving(true);
                try {
                  // Salvează seria pe item, apoi închide prompt-ul și retrigger submit (mutarea)
                  await api.put(`/inventory/${item.id}`, {
                    ...item,
                    serial: promptSerial.trim() || "",
                    no_serial: promptNoSerial,
                  });
                  // Actualizează local pe item.serial/no_serial pentru a permite submit-ul de mai sus
                  item.serial = promptSerial.trim() || "";
                  item.no_serial = promptNoSerial;
                  setNeedSerialPrompt(false);
                  // Re-rulează submit-ul mutării
                  setTimeout(() => submit(), 0);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Eroare la salvare");
                } finally { setPromptSaving(false); }
              }}
              disabled={promptSaving || (!promptSerial.trim() && !promptNoSerial)}
              className="bg-[var(--primary)]"
              data-testid="serial-prompt-save"
            >
              {promptSaving ? "Se salvează..." : "💾 Salvează & continuă mutarea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


// Panel cu istoricul item-ului — afișat în modul EDIT al ItemDialog
function ItemHistorySection({ item }) {
  const [history, setHistory] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRepairs, setLoadingRepairs] = useState(true);

  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    setLoading(true);
    setLoadingRepairs(true);
    api.get(`/inventory/${item.id}/history`)
      .then((r) => { if (!cancelled) setHistory(r.data || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    api.get(`/inventory/${item.id}/repairs`)
      .then((r) => { if (!cancelled) setRepairs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setRepairs([]); })
      .finally(() => { if (!cancelled) setLoadingRepairs(false); });
    return () => { cancelled = true; };
  }, [item?.id]);

  const actionLabel = (a) => ({
    create: "✅ Creat",
    move: "🔁 Mutat",
    edit: "✏ Editat",
    delete: "🗑 Șters",
    pickup: "📦 Preluat",
    return: "↩ Returnat",
    split_assign: "👤 Predat (split din stoc)",
    assign: "👤 Predat angajat",
  }[a] || a);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-bold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
        🕘 Istoric & detalii
      </div>

      {/* === Status defect/reparație curent === */}
      {(item.defect_marked_at || item.defect_repaired_at) && (
        <div className="space-y-1" data-testid="item-defect-status">
          {item.defect_marked_at && item.status === "defect" && (
            <div className="bg-red-50 border border-red-300 rounded p-2 text-xs">
              <div className="font-bold text-red-800 uppercase tracking-wider text-[10px]">⚠️ Defect raportat</div>
              <div className="text-zinc-800 mt-0.5">
                Pe data de <strong>{fmt(item.defect_marked_at)}</strong>
                {item.defect_marked_by && <> · raportat de <strong>{item.defect_marked_by}</strong></>}
              </div>
              {item.defect_reason && (
                <div className="text-zinc-700 italic mt-0.5">„{item.defect_reason}"</div>
              )}
              {Array.isArray(item.defect_image_ids) && item.defect_image_ids.length > 0 && (
                <div className="flex gap-1 mt-1.5 flex-wrap" data-testid="defect-images-thumbs">
                  {item.defect_image_ids.map((fid) => (
                    <a
                      key={fid}
                      href={fileUrl(fid)}
                      target="_blank"
                      rel="noreferrer"
                      title="Vezi poza defectului"
                    >
                      <img
                        src={fileUrl(fid, { w: 48 })}
                        alt="defect"
                        className="w-12 h-12 object-cover rounded border border-red-300 hover:border-red-500"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </a>
                  ))}
                </div>
              )}
              {Array.isArray(item.defect_video_ids) && item.defect_video_ids.length > 0 && (
                <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid="defect-videos-list">
                  {item.defect_video_ids.map((fid, idx) => (
                    <a
                      key={fid}
                      href={fileUrl(fid)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 bg-white text-red-800 text-[11px] font-semibold hover:bg-red-100"
                      title="Vezi filmarea defectului"
                    >
                      🎬 Filmare #{idx + 1}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          {item.defect_repaired_at && item.status === "functional" && (
            <div className="bg-emerald-50 border border-emerald-300 rounded p-2 text-xs">
              <div className="font-bold text-emerald-800 uppercase tracking-wider text-[10px]">✅ Ultima reparație</div>
              <div className="text-zinc-800 mt-0.5">
                Pe data de <strong>{fmt(item.defect_repaired_at)}</strong>
                {item.defect_repaired_by && <> · reparat de <strong>{item.defect_repaired_by}</strong></>}
                {item.defect_repair_duration_min && <> · durată <strong>{item.defect_repair_duration_min} min</strong></>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Datele de bază */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] bg-zinc-50 border border-zinc-200 rounded p-2">
        <div>
          <div className="text-zinc-500 font-semibold uppercase tracking-wider text-[9px]">Producție / Achiziție</div>
          <div className="font-mono">{fmt(item.purchase_date) || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-500 font-semibold uppercase tracking-wider text-[9px]">Garanție până</div>
          <div className="font-mono">{fmt(item.warranty_until) || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-500 font-semibold uppercase tracking-wider text-[9px]">Creat la</div>
          <div className="font-mono">{fmt(item.created_at)}</div>
        </div>
        <div>
          <div className="text-zinc-500 font-semibold uppercase tracking-wider text-[9px]">{item.assignee_person_id ? "Predat la" : "Locație"}</div>
          <div className="font-semibold">
            {item.assignee_person_id ? `👤 ${item.assignee || "—"}` : (item.location ? `📍 ${item.location}` : "—")}
            {item.assigned_at && (
              <span className="text-zinc-500 ml-1 text-[10px]">(din {fmt(item.assigned_at)})</span>
            )}
          </div>
        </div>
      </div>

      {/* === Istoric reparații (cu cine, ce, piese) === */}
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mt-2 flex items-center gap-1.5">
        🔧 Reparații ({repairs.length})
      </div>
      {loadingRepairs ? (
        <div className="text-xs text-zinc-500 italic p-2">Se încarcă reparațiile…</div>
      ) : repairs.length === 0 ? (
        <div className="text-xs text-zinc-500 italic p-2 bg-zinc-50 border border-zinc-200 rounded" data-testid="item-repairs-empty">
          Acest echipament nu a fost reparat încă.
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto border border-amber-200 bg-amber-50/40 rounded p-1.5" data-testid="item-repairs-list">
          {repairs.map((r, idx) => (
            <li
              key={r.id || idx}
              className="bg-white border border-amber-200 rounded px-2 py-2 hover:border-amber-400 transition-colors shadow-sm"
              data-testid={`item-repair-${r.id || idx}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-bold text-amber-900 text-xs">🔧 {r.date || "—"}</span>
                <div className="flex items-center gap-1">
                  {r.duration_min != null && r.duration_min > 0 && (
                    <span className="text-[10px] font-mono font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
                      ⏱ {r.duration_min} min
                    </span>
                  )}
                  {r.cost_chf != null && r.cost_chf !== "" && (
                    <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                      {Number(r.cost_chf).toFixed(2)} CHF
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[12px] text-zinc-800 mt-1 leading-snug whitespace-pre-line">
                {r.description || "—"}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-zinc-600">
                {r.performed_by && (
                  <span>
                    <span className="text-zinc-400">👤 Reparat de:</span>{" "}
                    <strong className="text-zinc-800">{r.performed_by}</strong>
                  </span>
                )}
                {r.parts_used && (
                  <span>
                    <span className="text-zinc-400">🔩 Piese:</span>{" "}
                    <strong className="text-zinc-800">{r.parts_used}</strong>
                  </span>
                )}
                {r.created_by && (
                  <span className="text-zinc-400 italic">
                    înregistrat de {r.created_by}
                    {r.created_at && <> · {fmt(r.created_at)}</>}
                  </span>
                )}
              </div>
              {Array.isArray(r.image_ids) && r.image_ids.length > 0 && (
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {r.image_ids.map((pid) => (
                    <a
                      key={pid}
                      href={fileUrl(pid)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block"
                      title="Vezi poza reparației"
                    >
                      <img
                        src={fileUrl(pid, { w: 48 })}
                        alt="reparație"
                        className="w-10 h-10 object-cover rounded border border-zinc-300 hover:border-amber-500"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Lista evenimentelor */}
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mt-1">Mișcări ({history.length})</div>
      {loading ? (
        <div className="text-xs text-zinc-500 italic p-2">Se încarcă istoricul…</div>
      ) : history.length === 0 ? (
        <div className="text-xs text-zinc-500 italic p-2 bg-zinc-50 border border-zinc-200 rounded">
          Nicio mișcare înregistrată încă pentru acest produs.
        </div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto border border-zinc-200 rounded">
          {history.map((h, idx) => (
            <li key={h.id || idx} className="text-[11px] px-2 py-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-cyan-50/40">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-zinc-800">{actionLabel(h.action)}</span>
                <span className="text-zinc-500 font-mono text-[10px]">{fmt(h.ts)}</span>
              </div>
              <div className="text-zinc-600 mt-0.5 text-[10px]">
                {h.from_location && <span>📍 <strong>{h.from_location}</strong></span>}
                {h.from_location && h.to_location && <span> → </span>}
                {h.to_location && <span>📍 <strong>{h.to_location}</strong></span>}
                {h.from_assignee && <span> · era la <strong>{h.from_assignee}</strong></span>}
                {h.to_assignee && <span> · → <strong>👤 {h.to_assignee}</strong></span>}
                {h.quantity && <span> · {h.quantity} buc.</span>}
                {h.user && <span className="ml-2 text-zinc-400">de {h.user}</span>}
              </div>
              {h.reason && <div className="text-[10px] text-zinc-500 italic mt-0.5">„{h.reason}"</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ============== ItemRepairsTopBanner ==============
// Banner prominent afișat sus în ItemDialog pentru a evidenția istoricul de reparații.
function ItemRepairsTopBanner({ item }) {
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!item?.id) return;
    let cancel = false;
    setLoading(true);
    api.get(`/inventory/${item.id}/repairs`)
      .then((r) => { if (!cancel) setRepairs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancel) setRepairs([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [item?.id]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
  };

  if (loading) return null;
  if (repairs.length === 0) return null;

  const recent = expanded ? repairs : repairs.slice(0, 2);
  return (
    <div
      className="rounded-md border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 p-3 mb-3"
      data-testid="item-repairs-top-banner"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-orange-200 text-orange-900 grid place-items-center text-lg">🔧</div>
          <div>
            <div className="font-display font-bold text-sm text-orange-900">
              {repairs.length} reparație{repairs.length === 1 ? "" : repairs.length < 20 && repairs.length > 1 ? "i" : ""} înregistrată{repairs.length === 1 ? "" : "e"}
            </div>
            <div className="text-[11px] text-orange-800">Ultima: {fmt((repairs[0] || {}).date)}{repairs[0]?.performed_by ? ` · de ${repairs[0].performed_by}` : ""}</div>
          </div>
        </div>
        {repairs.length > 2 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] font-bold text-orange-700 hover:text-orange-900 underline"
            data-testid="item-repairs-top-toggle"
          >
            {expanded ? "Vezi doar ultimele 2" : `Vezi toate (${repairs.length})`}
          </button>
        )}
      </div>
      <ul className="space-y-1.5">
        {recent.map((r, idx) => (
          <li
            key={r.id || idx}
            className="bg-white rounded border border-orange-200 p-2"
            data-testid={`item-repair-top-${r.id || idx}`}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-orange-900">📅 {fmt(r.date)}</span>
                {r.performed_by && (
                  <span className="text-[10px] font-semibold bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">
                    🔧 {r.performed_by}
                  </span>
                )}
              </div>
              {r.cost_chf != null && Number(r.cost_chf) > 0 && (
                <span className="text-[11px] font-bold text-emerald-700">{Number(r.cost_chf).toFixed(2)} CHF</span>
              )}
            </div>
            {r.description && (
              <div className="text-[12px] text-zinc-800 mt-0.5 leading-snug">{r.description}</div>
            )}
            {r.parts_used && (
              <div className="text-[10px] text-zinc-600 mt-0.5">
                <strong>Piese:</strong> {r.parts_used}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

